import moment from 'moment'
import * as XLSX from 'xlsx'

export type ShipFileRow = {
    transaction: string
    ship_date: string
}

const normalizeHeader = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ')

const isHpHeader = (header: string) => {
    const normalized = normalizeHeader(header)
    return normalized === 'hp' || normalized === 'transaction' || normalized === 'transacao'
}

const isShipDateHeader = (header: string) => {
    const normalized = normalizeHeader(header)
    return (
        normalized === 'data envio' ||
        normalized === 'data de envio' ||
        normalized === 'ship date' ||
        normalized === 'ship_date' ||
        normalized === 'envio'
    )
}

const formatShipDate = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    if (value instanceof Date) {
        const date = moment.utc([
            value.getUTCFullYear(),
            value.getUTCMonth(),
            value.getUTCDate(),
        ])
        return date.isValid() ? date.format('DD/MM/YYYY') : null
    }

    if (typeof value === 'number') {
        const parsed = XLSX.SSF.parse_date_code(value)
        if (!parsed) {
            return null
        }

        const date = moment({ year: parsed.y, month: parsed.m - 1, day: parsed.d })
        return date.isValid() ? date.format('DD/MM/YYYY') : null
    }

    const raw = String(value).trim()
    const date = moment(raw, ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD'], true)
    return date.isValid() ? date.format('DD/MM/YYYY') : null
}

const normalizeTransaction = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const transaction = String(value).trim().toUpperCase()
    return transaction || null
}

export const parseShipFile = async (file: File): Promise<ShipFileRow[]> => {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })
    const sheetName = workbook.SheetNames[0]

    if (!sheetName) {
        throw new Error('Planilha vazia.')
    }

    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
        defval: '',
        raw: true,
    })

    if (rows.length === 0) {
        throw new Error('Nenhuma linha encontrada na planilha.')
    }

    const headers = Object.keys(rows[0])
    const hpHeader = headers.find(isHpHeader)
    const shipDateHeader = headers.find(isShipDateHeader)

    if (!hpHeader || !shipDateHeader) {
        throw new Error('A planilha deve conter as colunas HP e DATA ENVIO.')
    }

    const parsed = rows
        .map((row) => {
            const transaction = normalizeTransaction(row[hpHeader])
            const ship_date = formatShipDate(row[shipDateHeader])

            if (!transaction && !ship_date) {
                return null
            }

            if (!transaction || !ship_date) {
                throw new Error(`Linha inválida na planilha (HP: ${transaction || '-'}, Data: ${ship_date || '-'}).`)
            }

            return { transaction, ship_date }
        })
        .filter((row): row is ShipFileRow => row !== null)

    if (parsed.length === 0) {
        throw new Error('Nenhuma linha válida encontrada na planilha.')
    }

    return parsed
}
